import { createTheme, Theme } from '@mui/material/styles';

export type ThemeName = 'dark' | 'light' | 'classic';

export interface ThemeConfig {
  name: ThemeName;
  displayName: string;
  theme: Theme;
}

type ThemeTokens = {
  accent: string;
  accentStrong: string;
  secondary: string;
  bg: string;
  surface: string;
  surfaceElevated: string;
  text: string;
  textMuted: string;
  textStrong: string;
  textInverse: string;
  heading: string;
  border: string;
  borderStrong: string;
  borderStronger: string;
  shadowCard: string;
  shadowPopover: string;
  shadowModal: string;
  danger: string;
  success: string;
  warning: string;
  info: string;
};

const darkTokens: ThemeTokens = {
  accent: '#ed8936',
  accentStrong: '#d97706',
  secondary: '#9c27b0',
  bg: '#12161e',
  surface: '#1a202c',
  surfaceElevated: '#223046',
  text: 'rgba(255, 255, 255, 0.9)',
  textMuted: 'rgba(255, 255, 255, 0.7)',
  textStrong: '#ffffff',
  textInverse: '#ffffff',
  heading: '#f8fafc',
  border: 'rgba(255, 255, 255, 0.08)',
  borderStrong: 'rgba(255, 255, 255, 0.2)',
  borderStronger: 'rgba(255, 255, 255, 0.3)',
  shadowCard: '0 2px 8px rgba(0, 0, 0, 0.3)',
  shadowPopover: '0 4px 20px rgba(0, 0, 0, 0.4)',
  shadowModal: '0 4px 24px rgba(0, 0, 0, 0.4)',
  danger: '#d32f2f',
  success: '#2e7d32',
  warning: '#ff9800',
  info: '#1976d2',
};

const classicTokens: ThemeTokens = {
  accent: '#001f43',
  accentStrong: '#001530',
  secondary: '#6d4aa0',
  bg: '#eaf2f8',
  surface: 'rgba(255, 255, 255, 0.85)',
  surfaceElevated: 'rgba(255, 255, 255, 0.95)',
  text: 'rgba(31, 41, 68, 0.92)',
  textMuted: 'rgba(31, 41, 68, 0.7)',
  textStrong: '#0f2540',
  textInverse: '#ffffff',
  heading: '#0f2540',
  border: 'rgba(31, 41, 68, 0.12)',
  borderStrong: 'rgba(31, 41, 68, 0.22)',
  borderStronger: 'rgba(31, 41, 68, 0.32)',
  shadowCard: '0 2px 10px rgba(31, 60, 110, 0.12)',
  shadowPopover: '0 6px 22px rgba(31, 60, 110, 0.18)',
  shadowModal: '0 8px 28px rgba(31, 60, 110, 0.22)',
  danger: '#c0392b',
  success: '#2f8a3a',
  warning: '#e08e0b',
  info: '#001f43',
};

const lightTokens: ThemeTokens = {
  accent: '#f07c2b',
  accentStrong: '#d86a1f',
  secondary: '#7c3aed',
  bg: '#e9eef5',
  surface: '#ffffff',
  surfaceElevated: '#f2f5f9',
  text: 'rgba(31, 41, 68, 0.9)',
  textMuted: 'rgba(31, 41, 68, 0.7)',
  textStrong: '#0f172a',
  textInverse: '#ffffff',
  heading: '#1f2a44',
  border: 'rgba(31, 41, 68, 0.12)',
  borderStrong: 'rgba(31, 41, 68, 0.2)',
  borderStronger: 'rgba(31, 41, 68, 0.3)',
  shadowCard: '0 2px 8px rgba(20, 44, 80, 0.12)',
  shadowPopover: '0 4px 20px rgba(20, 44, 80, 0.16)',
  shadowModal: '0 4px 24px rgba(20, 44, 80, 0.18)',
  danger: '#d32f2f',
  success: '#2e7d32',
  warning: '#f59e0b',
  info: '#3b82f6',
};

const buildTheme = (mode: 'dark' | 'light', tokensOverride?: ThemeTokens) => {
  const tokens = tokensOverride ?? (mode === 'dark' ? darkTokens : lightTokens);
  return createTheme({
    palette: {
      mode,
      primary: {
        main: tokens.accent,
        light: tokens.accentStrong,
        dark: tokens.accentStrong,
      },
      secondary: {
        main: tokens.secondary,
      },
      background: {
        default: tokens.bg,
        paper: tokens.surface,
      },
      text: {
        primary: tokens.text,
        secondary: tokens.textMuted,
      },
      error: {
        main: tokens.danger,
      },
      success: {
        main: tokens.success,
      },
      warning: {
        main: tokens.warning,
      },
      info: {
        main: tokens.info,
      },
      divider: tokens.border,
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h4: {
        fontWeight: 700,
        color: tokens.textStrong,
      },
      h6: {
        fontWeight: 600,
        color: tokens.textStrong,
      },
      body1: {
        color: tokens.text,
      },
      body2: {
        color: tokens.textMuted,
      },
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: tokens.surface,
            borderColor: tokens.border,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: tokens.surface,
            borderColor: tokens.border,
            boxShadow: tokens.shadowCard,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          contained: {
            backgroundColor: tokens.accent,
            color: tokens.textInverse,
            fontWeight: 600,
            '&:hover': {
              backgroundColor: tokens.accentStrong,
            },
            '&.Mui-disabled': {
              backgroundColor: mode === 'dark' ? 'rgba(237, 137, 51, 0.3)' : 'rgba(240, 124, 43, 0.3)',
              color: mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(31, 41, 68, 0.5)',
            },
          },
          outlined: {
            borderColor: tokens.borderStrong,
            color: tokens.text,
            '&:hover': {
              borderColor: tokens.accent,
              backgroundColor: mode === 'dark' ? 'rgba(237, 137, 51, 0.1)' : 'rgba(240, 124, 43, 0.12)',
            },
          },
        },
      },
      MuiSlider: {
        styleOverrides: {
          thumb: {
            // Keep the thumb attached to the rail while placing most of its
            // touch target below it on touch-sized layouts. Laptop and desktop
            // layouts retain MUI's centred thumb alignment. Neither rule
            // affects rail width or the spacing around surrounding controls.
            top: 'calc(50% + 6px)',
            '@media (min-width: 900px)': {
              top: '50%',
            },
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: tokens.surfaceElevated,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            color: tokens.heading,
            fontWeight: 600,
            borderBottomColor: tokens.border,
          },
          body: {
            color: tokens.text,
            borderColor: tokens.border,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            color: tokens.text,
            '& fieldset': {
              borderColor: tokens.borderStrong,
            },
            '&:hover fieldset': {
              borderColor: tokens.borderStronger,
            },
            '&.Mui-focused fieldset': {
              borderColor: tokens.accent,
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: tokens.textMuted,
            '&.Mui-focused': {
              color: tokens.accent,
            },
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            color: tokens.textMuted,
            borderColor: tokens.borderStrong,
            '&.Mui-selected': {
              backgroundColor: tokens.accent,
              color: tokens.textInverse,
              '&:hover': {
                backgroundColor: tokens.accentStrong,
              },
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: tokens.text,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: tokens.surface,
            border: `1px solid ${tokens.border}`,
            boxShadow: tokens.shadowModal,
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            backgroundColor: tokens.surface,
            border: `1px solid ${tokens.border}`,
            boxShadow: tokens.shadowPopover,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: tokens.surface,
            color: tokens.textStrong,
            boxShadow: tokens.shadowPopover,
            border: `1px solid ${tokens.border}`,
            fontSize: 12,
          },
          arrow: {
            color: tokens.surface,
          },
        },
      },
    },
  });
};

const darkTheme = buildTheme('dark');
const lightTheme = buildTheme('light');
const classicThemeBase = buildTheme('light', classicTokens);

// ---------------------------------------------------------------------------
// Project Essentials themes
// ---------------------------------------------------------------------------
// PE has its own dark/light/classic trio so Avenir typography stays applied
// across all three theme modes. The `light` and `dark` PE variants reuse the
// existing colour palettes verbatim — only the font family/weights change.
// `classic` carries the brand-navy palette defined in `classicTokens`.
//
// The Avenir webfont is declared in `public/index.html`; each variant below
// explicitly re-declares `fontFamily` because MUI variant overrides don't
// inherit the root `typography.fontFamily` once you start declaring others.
const AVENIR_STACK = '"Avenir", "Inter", "Roboto", "Helvetica", "Arial", sans-serif';

// PE light/dark reuse the existing light/dark token palettes verbatim —
// only the typography (Avenir) differs. Classic keeps its own brand-navy
// palette as defined above.
const withAvenirTypography = (base: Theme, tokens: ThemeTokens): Theme =>
  createTheme(base, {
    typography: {
      fontFamily: AVENIR_STACK,
      h1: { fontFamily: AVENIR_STACK, fontWeight: 900, color: tokens.heading, letterSpacing: '0.01em' },
      h2: { fontFamily: AVENIR_STACK, fontWeight: 900, color: tokens.heading, letterSpacing: '0.01em' },
      h3: { fontFamily: AVENIR_STACK, fontWeight: 900, color: tokens.heading, letterSpacing: '0.01em' },
      h4: { fontFamily: AVENIR_STACK, fontWeight: 900, color: tokens.heading, letterSpacing: '0.01em' },
      h5: { fontFamily: AVENIR_STACK, fontWeight: 900, color: tokens.heading, letterSpacing: '0.01em' },
      h6: { fontFamily: AVENIR_STACK, fontWeight: 900, color: tokens.heading, letterSpacing: '0.01em' },
      subtitle1: { fontFamily: AVENIR_STACK, fontWeight: 500, color: tokens.heading },
      subtitle2: { fontFamily: AVENIR_STACK, fontWeight: 500, color: tokens.heading },
      body1: { fontFamily: AVENIR_STACK, fontWeight: 500, color: tokens.text },
      body2: { fontFamily: AVENIR_STACK, fontWeight: 500, color: tokens.textMuted },
      button: { fontFamily: AVENIR_STACK, fontWeight: 500, letterSpacing: '0.03em' },
      caption: { fontFamily: AVENIR_STACK, fontWeight: 500 },
      overline: { fontFamily: AVENIR_STACK, fontWeight: 500 },
    },
  });

const classicTheme = withAvenirTypography(classicThemeBase, classicTokens);
const peLightTheme = withAvenirTypography(lightTheme, lightTokens);
const peDarkTheme = withAvenirTypography(darkTheme, darkTokens);

export const themes: Record<ThemeName, ThemeConfig> = {
  dark: {
    name: 'dark',
    displayName: 'Dark',
    theme: darkTheme,
  },
  light: {
    name: 'light',
    displayName: 'Light',
    theme: lightTheme,
  },
  classic: {
    name: 'classic',
    displayName: 'Classic',
    theme: classicTheme,
  },
};

// Parallel theme map consumed by the ThemeProvider when the active module
// is `project-essential`. Same `ThemeName` keys (`dark`/`light`/`classic`)
// so the existing storage/UI plumbing keeps working — the provider just
// resolves to a different MUI theme instance per module.
export const projectEssentialThemes: Record<ThemeName, ThemeConfig> = {
  dark: {
    name: 'dark',
    displayName: 'Dark',
    theme: peDarkTheme,
  },
  light: {
    name: 'light',
    displayName: 'Light',
    theme: peLightTheme,
  },
  classic: {
    name: 'classic',
    displayName: 'Classic',
    theme: classicTheme,
  },
};
